import React, { useEffect, useState } from "react";
import { Timer } from "lucide-react";
import { formatTaskDuration, parseTaskDuration } from "../../../lib/utils";
import { DURATION_PRESETS } from "../../../constants/durations";

export interface DurationPickerProps {
  /** Current duration in minutes, or null when unset. */
  value: number | null;
  /**
   * Commit a new duration. Fired on blur of the free-text field, on a preset
   * tap, and on Clear — never per keystroke, so a caller that batches writes
   * (the recurrence draft) stays in control of when a save happens.
   */
  onChange: (duration: number | null) => void;
}

/**
 * Duration control: a free-text field that accepts "30m" / "1h" / "1h 30m",
 * the one-tap presets, and a Clear affordance once a duration is set.
 *
 * Used by the task's Schedule section, the recurrence section and the sub-task
 * editor, which previously carried three near-identical copies of this markup.
 */
const DurationPicker: React.FC<DurationPickerProps> = ({ value, onChange }) => {
  const [durationInput, setDurationInput] = useState(
    formatTaskDuration(value),
  );

  // Keep the text field in step with the committed value — it changes from
  // outside this component whenever a preset is tapped, the schedule picker
  // applies a slot, or the dialog navigates to another task.
  useEffect(() => {
    setDurationInput(formatTaskDuration(value));
  }, [value]);

  return (
    <div>
      <div className="relative mb-2">
        <Timer
          className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-text-tertiary"
          size={16}
        />
        <input
          type="text"
          aria-label="Duration"
          placeholder="Duration e.g. 30m, 1h, 1h 30m"
          value={durationInput}
          onChange={(e) => setDurationInput(e.target.value)}
          onBlur={() => onChange(parseTaskDuration(durationInput))}
          className="w-full bg-warm-bg-elevated border border-warm-border rounded-soft pl-10 pr-3 py-2 text-sm text-warm-text-primary placeholder-warm-text-tertiary focus:outline-none focus:ring-2 focus:ring-warm-blue"
        />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {DURATION_PRESETS.map((mins) => (
          <button
            key={mins}
            type="button"
            onClick={() => onChange(mins)}
            className={`px-2.5 py-1 rounded-soft text-xs transition-all duration-150 ease-in-out ${
              value === mins
                ? "bg-warm-blue text-white"
                : "bg-warm-bg-elevated border border-warm-border text-warm-text-secondary hover:border-warm-blue/50 dark:hover:border-warm-blue/50"
            }`}
          >
            {formatTaskDuration(mins)}
          </button>
        ))}
        {value != null && value > 0 && (
          <button
            type="button"
            aria-label="Clear duration"
            onClick={() => onChange(null)}
            className="px-2.5 py-1 rounded-soft text-xs text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-150 ease-in-out"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
};

export default DurationPicker;
